import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { Subscription } from 'rxjs';
import { MenuItem } from '../../models/menu-item.model';
import { IUser, UserRole } from '../../models/user.model';
import { ORDER_DOCUMENT_ROLES, canAccess, DELIVERY_NOTE_ROLES } from '../../constants/role-access';
import { AuthService } from '../../services/auth.service';
import { SidebarService } from '../../services/sidebar.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @Input() currentUser: IUser | null = null;
  public isCollapsed = false;
  public menuItems: MenuItem[] = [
    { icon: 'dashboard', label: 'Dashboard', route: '/dashboard', active: false, roles: [] },
    { icon: 'list_alt', label: 'Aufträge', route: '/orders', active: false, roles: [UserRole.BUERO] },
    { icon: 'description', label: 'Auftragsbegleitschein', route: '/order-documents', active: false, roles: ORDER_DOCUMENT_ROLES },
    { icon: 'local_shipping', label: 'Lieferzettel', route: '/delivery-notes', active: false, roles: DELIVERY_NOTE_ROLES },
    // No `roles` key: the Liefertermine overview is open to every role.
    { icon: 'event', label: 'Liefertermine', route: '/delivery-dates', active: false },
    {
      icon: 'analytics',
      label: 'Statistiken',
      active: false,
      roles: [],
      expanded: false,
      children: [{ icon: 'list_alt', label: 'LZ-Statistiken', route: '/deliver-note-statistics', active: false, roles: [] }],
    },
    {
      icon: 'settings',
      label: 'Einstellungen',
      active: false,
      roles: [],
      expanded: false,
      children: [
        { icon: 'people', label: 'Benutzer', route: '/users', active: false, roles: [] },
        { icon: 'text_snippet', label: 'Textbausteine', route: '/text-blocks', active: false, roles: [] },
        { icon: 'cloud_upload', label: 'DMS-Export', route: '/dms-export', active: false, roles: [] },
      ],
    },
  ];

  public UserRole = UserRole;

  private subscription = new Subscription();

  constructor(
    private router: Router,
    private authService: AuthService,
    private sidebarService: SidebarService
  ) {}

  public ngOnInit(): void {
    this.setActiveMenuItem(this.router.url);

    this.subscription.add(
      this.router.events.subscribe(() => {
        this.setActiveMenuItem(this.router.url);
      })
    );

    this.subscription.add(
      this.sidebarService.isCollapsed$.subscribe(collapsed => {
        this.isCollapsed = collapsed;
      })
    );
  }

  /**
   * Whether the current user may see a menu item.
   * No `roles` → visible to everyone; otherwise the listed roles plus ADMIN
   * (an empty list means ADMIN only, since ADMIN always passes canAccess).
   */
  public canSee(item: MenuItem): boolean {
    if (!item.roles) return true;
    return canAccess(this.currentUser?.role, item.roles);
  }

  public toggleSubmenu(item: MenuItem): void {
    if (item.children) {
      item.expanded = !item.expanded;
    }
  }

  private setActiveMenuItem(currentRoute: string): void {
    this.menuItems = this.menuItems.map(item => ({
      ...item,
      active: item.route ? currentRoute.includes(item.route) : false,
      children: item.children
        ? item.children.map(child => ({
            ...child,
            active: child.route ? currentRoute.includes(child.route) : false,
          }))
        : undefined,
      expanded: item.children ? item.children.some(child => child.route && currentRoute.includes(child.route)) : false,
    }));
  }

  public logout(): void {
    this.authService.logout();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
